import { Injectable, signal } from '@angular/core';

/**
 * Shape of the alias-session fields carried as siblings of `data` on the
 * `/user/me` `ApiResponse` envelope (see `AuthenticationService.me()`). Only
 * the fields this service cares about are declared — the envelope also
 * carries `code`/`status`/`data`/etc., which callers pass through untouched.
 */
export interface AliasSessionContext {
  isAliasSession?: boolean;
  vendorName?: string | null;
  staffName?: string | null;
}

/**
 * Owns whether the current session is a staff "alias" (impersonation)
 * session — a read-only view of a vendor's portal, entered by an internal
 * staff member via Task 14's `AliasService`.
 *
 * Hydrated from the `/user/me` envelope (see `AuthenticationService.me()`),
 * which is confirmed on every guarded navigation, so this stays current as
 * the user moves around the app. Consumers (the staff read-only banner and
 * the 403 read-only interceptor) read the signals below; neither re-derives
 * alias state on its own.
 */
@Injectable({
  providedIn: 'root',
})
export class AliasSessionService {
  private readonly _isAliased = signal(false);
  private readonly _vendorName = signal<string | null>(null);
  private readonly _staffName = signal<string | null>(null);

  /** Whether the current session is a read-only staff alias session. */
  readonly isAliased = this._isAliased.asReadonly();
  /** The vendor whose portal is being viewed, when aliased; otherwise `null`. */
  readonly vendorName = this._vendorName.asReadonly();
  /** The staff member driving the alias session, when aliased; otherwise `null`. */
  readonly staffName = this._staffName.asReadonly();

  /**
   * Hydrate alias-session state from a user-context response (currently
   * `/user/me`). Non-alias contexts (missing/false `isAliasSession`) resolve
   * to a fully cleared state, so a stale name from a previous alias session
   * can never linger once the session is confirmed non-aliased.
   */
  hydrate(context: AliasSessionContext): void {
    const aliased = !!context.isAliasSession;
    this._isAliased.set(aliased);
    this._vendorName.set(aliased ? context.vendorName ?? null : null);
    this._staffName.set(aliased ? context.staffName ?? null : null);
  }

  /** Reset to the default non-aliased state. */
  clear(): void {
    this._isAliased.set(false);
    this._vendorName.set(null);
    this._staffName.set(null);
  }
}
